/**
 * PermGate — route/module permission gate for the shop-account + PIN model (Jose 2026-09-23).
 *
 * ONE shop account runs every iPad; the PIN operator's ROLE decides what they can open. This wraps a
 * business-side page (reports, inventory, financials, settings…) and shows a friendly "no access"
 * panel when the current operator lacks the permission — instead of letting a barber navigate straight
 * to /financial. Driven by usePermission(), which already resolves the PIN operator's role (falling
 * back to the auth account). The hard security floor is still the DB (shop account = staff can't read
 * financials, Phase 0); this is the UX gate on top of it.
 *
 * IMPORTANT: this only bites once wired to routes, AND owners/managers must be able to elevate via PIN
 * first — otherwise the shop account (staff) gets blocked from its own reports. Ship WITH the
 * PIN-elevation flow, never before it. BEHAVIOR-PIN: project-slik-permissions-auth-production-2026-09-21.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { usePermission } from '../hooks/usePermission';

interface PermGateProps {
  children: React.ReactNode;
  perm?: string;        // a hasPerm() key, e.g. 'viewReports', 'manageInventory', 'editSettings'
  managerUp?: boolean;  // require manager or owner
  ownerOnly?: boolean;  // require owner
  label?: string;       // what they tried to open, for the message
}

const NoAccess: React.FC<{ label?: string }> = ({ label }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <Lock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Not available on your access</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {label ? <>The <b>{label}</b> section is</> : 'This section is'} owner/manager only. Ask an owner to open it,
          or sign in with an owner PIN.
        </p>
        <button
          onClick={() => navigate('/calendar')}
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the calendar
        </button>
      </div>
    </div>
  );
};

// Module/route permission gating — enforced EVERYWHERE as of the prod one-shot (Jose 2026-09-26).
const GATE_ENFORCED = true;

const PermGate: React.FC<PermGateProps> = ({ children, perm, managerUp, ownerOnly, label }) => {
  const { hasPerm, isManager, isOwner } = usePermission();
  if (!GATE_ENFORCED) return <>{children}</>; // prod: open until the one-shot
  const allowed =
    (ownerOnly ? isOwner : true) &&
    (managerUp ? (isManager || isOwner) : true) &&
    (perm ? hasPerm(perm as Parameters<typeof hasPerm>[0]) : true);
  if (!allowed) return <NoAccess label={label} />;
  return <>{children}</>;
};

export default PermGate;
